import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Client from '../api/client';
import TopNav from '../components/Layout/TopNav';
import MerchantMapper from '../components/Categories/MerchantMapper';
import SearchBar from '../components/Transactions/SearchBar';


const MerchantMapping = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [merchants, setMerchants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    uncategorized: searchParams.get('uncategorized') === 'true'
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [merchs, cats] = await Promise.all([
        Client.getMerchants(filters),
        Client.getCategories()
      ]);
      setMerchants(Array.isArray(merchs) ? merchs : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setMerchants([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (value) => {
    setFilters(prev => {
      const updated = { ...prev, search: value };
      const params = new URLSearchParams(searchParams);
      if (value) params.set('search', value);
      else params.delete('search');
      setSearchParams(params);
      return updated;
    });
  };

  const handleToggleUncategorized = () => {
    setFilters(prev => {
      const updated = { ...prev, uncategorized: !prev.uncategorized };
      const params = new URLSearchParams(searchParams);
      if (updated.uncategorized) params.set('uncategorized', 'true');
      else params.delete('uncategorized');
      setSearchParams(params);
      return updated;
    });
  };

  const handleUpdateCategory = async (normalized, categoryName) => {
    setErrorMsg(null);
    const previousMerchants = merchants;
    setMerchants(prev => prev.map(m => m.normalized === normalized ? { ...m, category: categoryName } : m));
    
    try {
      await Client.updateMerchantCategory(normalized, categoryName);
    } catch (error) {
      console.error("Failed to update merchant category:", error);
      setMerchants(previousMerchants);
      setErrorMsg("Couldn't update category. Please try again.");
    }
  };

  const handleBulkUpdate = async (normalizedIds, categoryName) => {
    setErrorMsg(null);
    const previousMerchants = merchants;
    setMerchants(prev => prev.map(m => normalizedIds.includes(m.normalized) ? { ...m, category: categoryName } : m));
    try {
      await Client.bulkUpdateMerchants(normalizedIds, categoryName);
    } catch (error) {
      console.error("Failed to bulk update merchants:", error);
      setMerchants(previousMerchants);
      setErrorMsg("Couldn't update category. Please try again.");
    }
  };

  const uncatCount = (Array.isArray(merchants) ? merchants : []).filter(m => m.category === 'Uncategorized').length;

  return (
    <div className="min-h-full bg-primary-bg pb-10 flex flex-col">
      <TopNav 
        title="Merchant Mapping"
        meta="Map merchants to categories for automatic categorization"
      />
      
      <div className="px-6 lg:px-10 py-6 max-w-7xl mx-auto w-full space-y-6 flex-1 flex flex-col animate-in fade-in duration-300">
        {errorMsg && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
            <strong>Error:</strong> {errorMsg}
          </div>
        )}
        
        {/* Help Banner */}
        <div className="glass-panel p-4 flex gap-3 items-start border border-accent/20 bg-[#6C63FF]/5 rounded-2xl">
          <span className="text-lg">💡</span>
          <div>
            <h4 className="text-sm font-bold text-white mb-0.5">Map Merchants to Categories</h4>
            <p className="text-xs text-text-muted leading-relaxed">
              This page lists unique merchants from both debit (payments) and credit (receipts) transactions. Map them to categories for automatic transaction categorization.
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-[#0c0c14] border border-white/10 p-4 rounded-2xl shadow-md">
          <SearchBar value={filters.search} onChange={handleSearchChange} />
          
          <button 
            onClick={handleToggleUncategorized}
            className={`px-4 py-2 border rounded-xl text-sm font-medium transition-all ${
              filters.uncategorized 
                ? 'bg-warning/10 border-warning text-warning' 
                : 'bg-secondary-bg border-border text-text-muted hover:border-text-muted hover:text-text-primary'
            }`}
          >
            {filters.uncategorized ? 'Showing Uncategorized' : `🔴 ${uncatCount > 0 ? uncatCount : 'No'} Uncategorized`}
          </button>
        </div>

        {loading ? (
          <div className="w-full flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        ) : (
          <MerchantMapper 
            merchants={
              filters.uncategorized 
                ? merchants.filter(m => m.category === 'Uncategorized') 
                : merchants
            }
            categories={categories}
            onUpdateCategory={handleUpdateCategory}
            onBulkUpdate={handleBulkUpdate}
            filters={filters}
          />
        )}
      </div>
    </div>
  );
};

export default MerchantMapping;
